import { Divider, Grid, Loader } from "@aws-amplify/ui-react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";

import { createEquipmentPhoto, deleteEquipmentPhoto } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import { equipmentPhotosByEquipmentID } from "graphql/queries";
import Gallery from "components/utils/Gallery";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import { dataURItoBlob, readFileAsDataURL } from "components/utils/functions";

const EquipmentPhotos = ({ equipmentID }) => {
  const [canUpload, canDelete] = usePermissions([
    "equipment:upload-photo",
    "equipment:delete-photo",
  ]);
  const [loading, setLoading] = useState(false);
  const [equipmentPhotos, setEquipmentPhotos] = useState([]);
  const [equipmentPhotosLoading, setEquipmentPhotosLoading] = useState(false);
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    equipmentPhotoId: null,
  });

  async function onCreate(fileObj) {
    try {
      setLoading(true);

      let fileSrc = "";
      let filePreview = "";

      if (typeof fileObj === "string") {
        fileSrc = `photo_${Date.now()}.jpeg`;
        const blob = dataURItoBlob(fileObj);

        await Storage.put(`equipment-photos/${fileSrc}`, blob, {
          contentType: blob.type,
          level: "public",
        });

        filePreview = fileObj;
      } else if (fileObj) {
        fileSrc = `${fileObj.name
          .split(".")
          .slice(0, -1)
          .join(".")}_${Date.now()}.${fileObj.name.split(".").slice(-1)[0]}`;

        await Storage.put(`equipment-photos/${fileSrc}`, fileObj, {
          contentType: fileObj.type,
          level: "public",
        });

        filePreview = await readFileAsDataURL(fileObj);
      }

      const newPhoto = (
        await API.graphql({
          query: createEquipmentPhoto.replaceAll("__typename", ""),
          variables: {
            input: {
              equipmentID,
              photo: fileSrc,
            },
          },
        })
      ).data.createEquipmentPhoto;

      setEquipmentPhotos((prev) => [
        ...(prev ?? []),
        { ...newPhoto, photo: filePreview },
      ]);

      toast.success(strings.successAdded);
    } catch (err) {
      console.log("Error Upload Photo", err);
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  }

  async function fetchEquipmentPhotos(id) {
    try {
      setEquipmentPhotosLoading(true);
      const equipmentPhotosData = await API.graphql(
        graphqlOperation(equipmentPhotosByEquipmentID, {
          equipmentID: id,
        })
      );
      const equipmentPhotosItems = equipmentPhotosData.data.equipmentPhotosByEquipmentID.items;

      const newList = await Promise.all(
        equipmentPhotosItems?.map(async (equipmentPhoto) => {
          const src = await Storage.get(`equipment-photos/${equipmentPhoto.photo}`, {
            level: "public",
          });
          return {
            ...equipmentPhoto,
            photo: src,
          };
        }) || []
      );

      setEquipmentPhotos(newList);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setEquipmentPhotosLoading(false);
    }
  }

  async function equipmentPhotoDelete(id) {
    try {
      setLoading(true);

      const result = await API.graphql(
        graphqlOperation(deleteEquipmentPhoto, { input: { id } })
      );
      const { photo: deletedSrc } = result.data.deleteEquipmentPhoto;
      if (deletedSrc)
        await Storage.remove(`equipment-photos/${deletedSrc}`, {
          level: "public",
        });

      setEquipmentPhotos(equipmentPhotos.filter((item) => item.id !== id));
      setModalDetails({
        visible: false,
        equipmentPhotoId: null,
      });
      toast.success(strings.successDeleted);
    } catch (err) {
      toast.error(strings.responses[err]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchEquipmentPhotos(equipmentID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentID]);

  return (
    <>
      <div>
        <div className="p-3">
          {equipmentPhotosLoading ? (
            <Loader />
          ) : (
            <Grid rowGap="15px" columnGap="15px">
              {Object.entries(strings.gallery.tags).map((gal, index) => (
                <>
                  {index > 0 && <Divider className="mt-3 mb-2" />}
                  <Gallery
                    images={
                      equipmentPhotos
                        .sort(
                          (a, b) =>
                            new Date(a.createdAt) - new Date(b.createdAt)
                        )
                        .map((photoModel) => ({
                          id: photoModel.id,
                          src: photoModel.photo,
                        })) ?? []
                    }
                    onCreate={canUpload && ((photo) => onCreate(photo))}
                    onDelete={canDelete && equipmentPhotoDelete}
                  />
                </>
              ))}
            </Grid>
          )}
        </div>
        <ConfirmModal
          visible={modalDetails.visible}
          body={strings.formatString(strings.modal.confirmDeleteQuestion)}
          onCancel={() =>
            setModalDetails({
              visible: false,
              equipmentPhotoId: null,
            })
          }
          onConfirm={() => equipmentPhotoDelete(modalDetails.equipmentPhotoId)}
        />
      </div>
      {loading && <LoaderFullScreen />}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Fotos del Equipo",
    gallery: {
      tags: {
        all: "Todos",
      },
    },
    successAdded: "Foto agregada con éxito",
    successDeleted: "Foto eliminada con éxito",
    responses: {
      errorFetch: "Error al consultar fotos del equipo",
      unexpectedError: "Ha ocurrido un error inesperado",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar esta foto?",
    },
  },
});

export default EquipmentPhotos;
